/**
 * Public, shareable Video API quickstart guide at /developers/quickstart.
 *
 * Renders the canonical guide markdown (kept in
 * packages/api-server/docs/v1-videos/QUICKSTART.md, copied here for the
 * bundle) as a styled article with copy-able code blocks and an "on this page"
 * nav. This route is intentionally PUBLIC: it makes no authenticated API calls,
 * so the 401 -> /login interceptor never fires, and it does not use AuthGuard.
 * That lets it be shared with prospects who have no Ivy login yet.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowUpRight, Copy, Check } from 'lucide-react';
import guideMd from '../content/quickstart.md?raw';

// Trailing underscore on `developers_` opts this route OUT of the /developers
// layout (developers.tsx has no <Outlet/>). The URL stays /developers/quickstart.
export const Route = createFileRoute('/developers_/quickstart')({
  component: QuickstartGuidePage,
});

/** Rewrite repo-relative links/images to their hosted public paths. */
function fixUrl(url?: string): string | undefined {
  if (!url) return url;
  if (url === './openapi.yaml') return 'https://api-aws.4klabs.ai/api/v1/openapi.yaml';
  if (url.startsWith('./')) return url.replace('./', '/developers/'); // ./img/x.png, ./example-client.mjs
  return url;
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function toText(node: ReactNode): string {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toText).join('');
  if (typeof node === 'object' && 'props' in (node as any)) return toText((node as any).props?.children);
  return '';
}

/** A code block with a one-click copy button. */
function CodeBlock({ children }: { children?: ReactNode }) {
  const ref = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const text = ref.current?.innerText ?? '';
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="group relative my-5">
      <button
        onClick={copy}
        aria-label="Copy code"
        className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/90 px-2 py-1 text-xs font-medium text-slate-200 opacity-60 transition-opacity hover:opacity-100 focus:opacity-100"
      >
        {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
      </button>
      <pre
        ref={ref}
        className="rounded-lg bg-slate-900 p-4 overflow-x-auto text-[13px] leading-relaxed"
        style={{ textShadow: 'none' }}
      >
        {children}
      </pre>
    </div>
  );
}

function QuickstartGuidePage() {
  // "On this page" nav, built from the markdown's ## headings.
  const toc = useMemo(
    () =>
      guideMd
        .split('\n')
        .filter((l) => l.startsWith('## '))
        .map((l) => l.slice(3).trim())
        .map((title) => ({ title, id: slug(title) })),
    [],
  );

  return (
    <div className="h-full overflow-y-auto bg-background">
      {/* Slim top bar with a clear call to action */}
      <header className="sticky top-0 z-20 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-6xl mx-auto px-6 md:px-8 h-14 flex items-center justify-between">
          <a href="https://app.4klabs.ai" className="font-semibold tracking-tight">
            4K Labs <span className="text-muted-foreground font-normal">· Ivy Video API</span>
          </a>
          <a
            href="https://app.4klabs.ai/developers"
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Get your API token
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 md:px-8 lg:grid lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-10">
        <article
          className="prose dark:prose-invert min-w-0 max-w-3xl py-10
                     prose-headings:scroll-mt-20 prose-img:rounded-lg prose-img:border
                     prose-code:before:content-none prose-code:after:content-none"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ children }) => <h2 id={slug(toText(children))}>{children}</h2>,
              a: ({ href, children, ...props }) => {
                const fixed = fixUrl(href);
                const external = fixed?.startsWith('http');
                return (
                  <a href={fixed} {...(external ? { target: '_blank', rel: 'noreferrer' } : {})} {...props}>
                    {children}
                  </a>
                );
              },
              img: ({ src, alt }) => (
                <img src={fixUrl(typeof src === 'string' ? src : undefined)} alt={alt ?? ''} loading="lazy" />
              ),
              // Block code blocks get a copy button (see CodeBlock).
              pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
              // DROP the language-* class so block code never matches the global
              // Prism light theme (color:#000 + white text-shadow); force readable
              // colors inline. Inline code gets a subtle chip.
              code: ({ className, children, ...props }) => {
                const raw = Array.isArray(children) ? children.join('') : String(children ?? '');
                const isBlock = /\blanguage-/.test(className || '') || raw.includes('\n');
                if (isBlock) {
                  return (
                    <code {...props} style={{ textShadow: 'none', background: 'transparent', color: '#e5e7eb' }}>
                      {children}
                    </code>
                  );
                }
                return (
                  <code
                    {...props}
                    className="rounded bg-muted px-1.5 py-0.5 text-[0.85em] font-medium"
                    style={{ textShadow: 'none' }}
                  >
                    {children}
                  </code>
                );
              },
            }}
          >
            {guideMd}
          </ReactMarkdown>
        </article>

        {/* On this page */}
        <aside className="hidden lg:block py-10">
          <div className="sticky top-20 text-sm">
            <div className="font-semibold text-muted-foreground mb-3 text-xs uppercase tracking-wide">On this page</div>
            <nav className="space-y-1.5 border-l border-border">
              {toc.map((h) => (
                <a
                  key={h.id}
                  href={`#${h.id}`}
                  className="block -ml-px border-l border-transparent pl-3 text-muted-foreground hover:border-primary hover:text-foreground transition-colors"
                >
                  {h.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </div>
  );
}
